import React, { useEffect, useMemo, useState } from 'react';
import { FiHome, FiLayers, FiBook, FiFileText, FiPlus, FiEdit, FiTrash2, FiEye, FiUpload } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showToast } from '../../components/common/Toaster';
import { publisherGradeAPI, publisherSubjectAPI, publisherPublicationAPI } from '../../api/publisherEndpoints';
import { formatDate, formatFileSize } from '../../utils/helpers';
import {
  DashboardContainer,
  DashboardHeader,
  SectionTitle,
  ContentGrid,
  ContentCard,
  ContentCardHeader,
  ContentCardBody,
  ContentCardFooter,
  ContentActions,
  ActionButton,
  EmptyState,
  FilterBar,
  SearchInput,
  FilterSelect,
  FileUploadArea,
} from '../dashboards/Dashboard.styles';

const PublisherPublications = () => {
  const [loading, setLoading] = useState(true);
  const [publications, setPublications] = useState([]);
  const [pagination, setPagination] = useState({});
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingPublication, setEditingPublication] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    grade_id: '',
    subject_id: '',
    pdf: null,
  });

  const [filters, setFilters] = useState({
    search: '',
    grade_id: '',
    subject_id: '',
    created_from: '',
    created_to: '',
    sort_by: 'created_at',
    sort_order: 'desc',
    page: 1,
    per_page: 20,
  });

  const menuItems = [
    { icon: <FiHome />, label: 'Dashboard', path: '/publisher' },
    { icon: <FiFileText />, label: 'Publications', path: '/publisher/publications' },
    { icon: <FiLayers />, label: 'Grades', path: '/publisher/grades' },
    { icon: <FiBook />, label: 'Subjects', path: '/publisher/subjects' },
  ];

  const gradeMap = useMemo(
    () => new Map(grades.map((grade) => [grade.id, grade.name])),
    [grades]
  );
  const subjectMap = useMemo(
    () => new Map(subjects.map((subject) => [subject.id, subject.name])),
    [subjects]
  );

  useEffect(() => {
    fetchReferenceData();
  }, []);

  useEffect(() => {
    fetchPublications();
  }, [filters]);

  const fetchReferenceData = async () => {
    try {
      setLoading(true);
      const [gradesRes, subjectsRes] = await Promise.all([
        publisherGradeAPI.getGrades({ page: 1, per_page: 200 }),
        publisherSubjectAPI.getSubjects({ page: 1, per_page: 200 }),
      ]);
      setGrades(gradesRes.data?.grades || []);
      setSubjects(subjectsRes.data?.subjects || []);
    } catch (error) {
      showToast('error', 'Failed to load grades and subjects');
    } finally {
      setLoading(false);
    }
  };

  const fetchPublications = async () => {
    try {
      setLoading(true);
      const response = await publisherPublicationAPI.getPublications(filters);
      setPublications(response.data?.publications || []);
      setPagination(response.data?.pagination || {});
    } catch (error) {
      showToast('error', 'Failed to load publications');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', grade_id: '', subject_id: '', pdf: null });
    setEditingPublication(null);
    setShowModal(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.pdf) {
      showToast('error', 'Please upload a PDF file');
      return;
    }
    setSubmitting(true);

    try {
      const form = new FormData();
      form.append('title', formData.title);
      form.append('grade_id', formData.grade_id);
      form.append('subject_id', formData.subject_id);
      form.append('pdf', formData.pdf);

      await publisherPublicationAPI.createPublication(form);
      showToast('success', 'Publication created successfully');
      resetForm();
      fetchPublications();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to create publication');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingPublication) return;
    setSubmitting(true);

    try {
      if (formData.pdf) {
        const form = new FormData();
        form.append('title', formData.title);
        form.append('grade_id', formData.grade_id);
        form.append('subject_id', formData.subject_id);
        form.append('pdf', formData.pdf);
        await publisherPublicationAPI.updatePublication(editingPublication.id, form);
      } else {
        await publisherPublicationAPI.updatePublication(editingPublication.id, {
          title: formData.title,
          grade_id: formData.grade_id,
          subject_id: formData.subject_id,
        });
      }
      showToast('success', 'Publication updated successfully');
      resetForm();
      fetchPublications();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to update publication');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (publication) => {
    setEditingPublication(publication);
    setFormData({
      title: publication.title || '',
      grade_id: publication.grade_id || publication.grade?.id || '',
      subject_id: publication.subject_id || publication.subject?.id || '',
      pdf: null,
    });
    setShowModal(true);
  };

  const handleDelete = async (publicationId, title) => {
    if (!window.confirm(`Delete "${title}"? This will remove the PDF.`)) return;

    try {
      await publisherPublicationAPI.deletePublication(publicationId);
      showToast('success', 'Publication deleted successfully');
      fetchPublications();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to delete publication');
    }
  };

  const renderFileLabel = () => {
    if (formData.pdf) {
      return `${formData.pdf.name} (${formatFileSize(formData.pdf.size)})`;
    }
    return editingPublication ? 'Upload new PDF (optional)' : 'Upload PDF (required)';
  };

  return (
    <DashboardLayout menuItems={menuItems}>
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>Publications</h1>
            <p>Upload and manage publisher PDFs with grade and subject tags</p>
          </div>
          <Button leftIcon={<FiPlus />} onClick={() => setShowModal(true)}>
            New Publication
          </Button>
        </DashboardHeader>

        <FilterBar>
          <SearchInput
            placeholder="Search titles..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
          />
          <FilterSelect
            value={filters.grade_id}
            onChange={(e) => setFilters({ ...filters, grade_id: e.target.value, page: 1 })}
          >
            <option value="">All Grades</option>
            {grades.map((grade) => (
              <option key={grade.id} value={grade.id}>{grade.name}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={filters.subject_id}
            onChange={(e) => setFilters({ ...filters, subject_id: e.target.value, page: 1 })}
          >
            <option value="">All Subjects</option>
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>{subject.name}</option>
            ))}
          </FilterSelect>
          <input
            type="date"
            value={filters.created_from}
            onChange={(e) => setFilters({ ...filters, created_from: e.target.value, page: 1 })}
            style={{
              padding: '12px 16px',
              border: '2px solid #E5E7EB',
              borderRadius: '12px',
              fontSize: '14px',
              background: '#F9FAFB',
              minWidth: '160px',
            }}
          />
          <input
            type="date"
            value={filters.created_to}
            onChange={(e) => setFilters({ ...filters, created_to: e.target.value, page: 1 })}
            style={{
              padding: '12px 16px',
              border: '2px solid #E5E7EB',
              borderRadius: '12px',
              fontSize: '14px',
              background: '#F9FAFB',
              minWidth: '160px',
            }}
          />
          <FilterSelect
            value={filters.sort_by}
            onChange={(e) => setFilters({ ...filters, sort_by: e.target.value, page: 1 })}
          >
            <option value="created_at">Sort by Date</option>
            <option value="title">Sort by Title</option>
            <option value="updated_at">Sort by Updated</option>
          </FilterSelect>
          <FilterSelect
            value={filters.sort_order}
            onChange={(e) => setFilters({ ...filters, sort_order: e.target.value, page: 1 })}
          >
            <option value="desc">Newest</option>
            <option value="asc">Oldest</option>
          </FilterSelect>
        </FilterBar>

        <SectionTitle>All Publications</SectionTitle>

        {loading ? (
          <LoadingSpinner size="large" text="Loading publications..." />
        ) : publications.length === 0 ? (
          <EmptyState>
            <FiFileText size={48} />
            <h3>No publications found</h3>
            <p>Upload your first PDF to build your publisher catalog</p>
            <Button leftIcon={<FiPlus />} onClick={() => setShowModal(true)}>
              Upload Publication
            </Button>
          </EmptyState>
        ) : (
          <ContentGrid>
            {publications.map((publication) => (
              <ContentCard key={publication.id}>
                <ContentCardHeader>
                  <div>
                    <h3>{publication.title}</h3>
                    <span className="badge" style={{ background: '#2c5f3420', color: '#2c5f34' }}>
                      PDF
                    </span>
                  </div>
                </ContentCardHeader>
                <ContentCardBody>
                  <p>
                    {subjectMap.get(publication.subject_id) || publication.subject?.name || 'Subject'} /{' '}
                    {gradeMap.get(publication.grade_id) || publication.grade?.name || 'Grade'}
                  </p>
                  <div className="meta">
                    <span>{formatFileSize(publication.file_size || 0)}</span>
                    <span>{formatDate(publication.created_at)}</span>
                  </div>
                </ContentCardBody>
                <ContentCardFooter>
                  <ContentActions>
                    <ActionButton title="View PDF" onClick={() => publication.pdf_url && window.open(publication.pdf_url, '_blank')}>
                      <FiEye />
                    </ActionButton>
                    <ActionButton title="Edit" onClick={() => handleEdit(publication)}>
                      <FiEdit />
                    </ActionButton>
                    <ActionButton title="Delete" onClick={() => handleDelete(publication.id, publication.title)}>
                      <FiTrash2 />
                    </ActionButton>
                  </ContentActions>
                </ContentCardFooter>
              </ContentCard>
            ))}
          </ContentGrid>
        )}

        {pagination?.pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
            <Button
              variant="ghost"
              disabled={pagination.page === 1}
              onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
            >
              Previous
            </Button>
            <span style={{ display: 'flex', alignItems: 'center', padding: '0 12px' }}>
              Page {pagination.page} of {pagination.pages}
            </span>
            <Button
              variant="ghost"
              disabled={pagination.page === pagination.pages}
              onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
            >
              Next
            </Button>
          </div>
        )}

        <Modal
          isOpen={showModal}
          onClose={resetForm}
          title={editingPublication ? 'Edit Publication' : 'Create Publication'}
          size="large"
        >
          <form onSubmit={editingPublication ? handleUpdate : handleCreate}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <Input
                label="Title"
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Revision Pack"
                required
              />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Grade</label>
                  <select
                    value={formData.grade_id}
                    onChange={(e) => setFormData({ ...formData, grade_id: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #E5E7EB',
                      borderRadius: '12px',
                      fontSize: '16px',
                      backgroundColor: '#F9FAFB',
                    }}
                    required
                  >
                    <option value="">Select grade</option>
                    {grades.map((grade) => (
                      <option key={grade.id} value={grade.id}>{grade.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Subject</label>
                  <select
                    value={formData.subject_id}
                    onChange={(e) => setFormData({ ...formData, subject_id: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #E5E7EB',
                      borderRadius: '12px',
                      fontSize: '16px',
                      backgroundColor: '#F9FAFB',
                    }}
                    required
                  >
                    <option value="">Select subject</option>
                    {subjects.map((subject) => (
                      <option key={subject.id} value={subject.id}>{subject.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <FileUploadArea>
                <label>
                  <FiUpload size={28} />
                  <h4>{renderFileLabel()}</h4>
                  <p>PDF files only</p>
                  <input
                    type="file"
                    accept="application/pdf"
                    style={{ display: 'none' }}
                    onChange={(e) => setFormData({ ...formData, pdf: e.target.files?.[0] || null })}
                  />
                </label>
              </FileUploadArea>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <Button variant="ghost" type="button" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" loading={submitting}>
                  {editingPublication ? 'Update Publication' : 'Create Publication'}
                </Button>
              </div>
            </div>
          </form>
        </Modal>
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default PublisherPublications;
